import { useEffect, useRef, useState } from 'react';
import { useOrganization, useSession, useUser } from '@clerk/react';
import { useQueryClient } from 'react-query';

import {
  clearPendingRegistration,
  linkClerkOrg,
  readPendingTenantId,
} from '@/api/tenants/linkClerkOrg';
import { useAuth } from '@/modules/Auth/hooks/useAuth';
import { isClerkAuthEnabled } from '@/utils/auth/authMode';

/**
 * After Paddle + POST /registration, links the pre-created tenant to the user's active Clerk org
 * before dashboard API calls run (avoids provisioning a duplicate tenant).
 */
export function useLinkPendingClerkTenant(): {
  isLinking: boolean;
  isLinkComplete: boolean;
  linkError: string | null;
} {
  const { isAuthenticated } = useAuth();
  const { session } = useSession();
  const { user } = useUser();
  const { organization, isLoaded: isOrgLoaded } = useOrganization();
  const queryClient = useQueryClient();
  const [isLinking, setIsLinking] = useState(false);
  const [linkError, setLinkError] = useState<string | null>(null);
  const linkInFlightRef = useRef(false);
  const [isLinkComplete, setIsLinkComplete] = useState(() => {
    if (!isClerkAuthEnabled()) return true;
    return !readPendingTenantId();
  });

  useEffect(() => {
    if (!isClerkAuthEnabled() || !isAuthenticated || !isOrgLoaded) {
      return;
    }

    const pendingTenantId = readPendingTenantId();
    if (!pendingTenantId) {
      setIsLinking(false);
      setIsLinkComplete(true);
      setLinkError(null);
      return;
    }

    if (!organization?.id) {
      setIsLinkComplete(false);
      return;
    }

    if (linkInFlightRef.current) {
      return;
    }

    let cancelled = false;
    linkInFlightRef.current = true;
    setIsLinking(true);
    setIsLinkComplete(false);
    setLinkError(null);

    (async () => {
      try {
        await session?.reload();
        await linkClerkOrg(pendingTenantId, organization.id);
        clearPendingRegistration();
        console.info('[ChurnSignal] Linked Clerk org to registration tenant:', pendingTenantId);

        // Backend may have PATCHed first/last onto the Clerk user — refresh client cache
        // so UserProfile shows names on the first open.
        try {
          await user?.reload();
        } catch (reloadError) {
          console.warn('[ChurnSignal] Clerk user.reload after link failed:', reloadError);
        }

        // Update UI before invalidateQueries — session.reload() can re-run this effect and
        // mark the run cancelled; linking already succeeded so always clear the spinner.
        setIsLinkComplete(true);
        setLinkError(null);
        setIsLinking(false);
        void queryClient.invalidateQueries();
      } catch (error) {
        const message =
          error instanceof Error ? error.message : 'Failed to link your company to this organization.';
        console.error('[ChurnSignal] Failed to link Clerk org to registration tenant:', error);
        if (!cancelled) {
          setLinkError(message);
          setIsLinkComplete(false);
        }
      } finally {
        linkInFlightRef.current = false;
        setIsLinking(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [isAuthenticated, isOrgLoaded, organization?.id, queryClient, session?.id, user]);

  return { isLinking, isLinkComplete, linkError };
}

/** True while Path B registration is waiting to link a pre-created tenant (blocks tenant-scoped API). */
export function isPendingClerkTenantLink(): boolean {
  if (!isClerkAuthEnabled()) return false;
  return Boolean(readPendingTenantId());
}
